import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import type {MilestoneState} from '@github-ui/server-persisted-queries/types/RepositoryMilestoneIndexPageQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {buildDeleteMilestoneOperation} from '../utils/milestone-connection-operations'

type UseDeleteMilestoneOptions = {
  repositoryId: string
  milestoneId: string
  milestoneNumber: number
  nameWithOwner: string
  /** The milestone's state, so the correct Open/Closed tab count is decremented. */
  state: MilestoneState
  /** The data-router index route query key, so the confirmed removal can be folded into base. */
  queryKey?: RouteQueryKey
  onSuccess?: () => void | Promise<void>
  onError?: (error: Error) => void
}

/**
 * Deletes a milestone via TanStack Query. Not optimistic — the row and dialog stay mounted until the
 * server responds, so a failure surfaces in the dialog's error Banner.
 */
export function useDeleteMilestoneMutation({
  repositoryId,
  milestoneId,
  milestoneNumber,
  nameWithOwner,
  state,
  queryKey,
  onSuccess,
  onError,
}: UseDeleteMilestoneOptions) {
  const queryClient = useQueryClient()
  const operation = buildDeleteMilestoneOperation(milestoneId, state)

  const {mutate} = useMutation<void, Error>({
    mutationFn: async () => {
      let response: Response
      try {
        response = await reactFetchJSON(`/${nameWithOwner}/milestones/${milestoneNumber}`, {
          method: 'DELETE',
        })
      } catch {
        throw new Error('Could not delete Milestone')
      }

      if (!response.ok) {
        throw new Error('Could not delete Milestone')
      }

      if (response.status !== 204) {
        const result = await response.json()
        if (result.error) {
          throw new Error('Could not delete Milestone')
        }
      }
    },
    onError,
    onSuccess: async () => {
      // Settle the confirmed removal into the current list before the pending overlay drops, using
      // the shared contract: cancel any in-flight revalidation first, so a stale-while-revalidate
      // fetch started before the DELETE cannot resolve afterward and reinsert the row.
      if (queryKey) {
        await settleConfirmedOperation({
          queryClient,
          targets: [{queryKey}],
          ownerId: repositoryId,
          operation,
        })
      }
      await onSuccess?.()
      // A later route-level refetch can race the completed DELETE. Restore the confirmed removal
      // if that response still includes the row, without decrementing a fresh response twice.
      if (queryKey) {
        await settleConfirmedOperation({
          queryClient,
          targets: [{queryKey}],
          ownerId: repositoryId,
          operation,
        })
      }
    },
  })

  return useCallback(() => {
    mutate()
  }, [mutate])
}
